import React, { ReactNode } from 'react';
import { TokensManager } from 'utils/utils';

// ================================= Вспомогательные

export type OffsetLimit = {
  offset: number;
  limit: number;
};

export type Status = 'pending' | 'rejected' | 'fulfilled';

export type ResourceSortType =
  | 'default'
  | '-likes'
  | '%2Blikes'
  | '-views'
  | '%2Bviews'
  | '-downloads'
  | '%2Bdownloads'
  | '-created_at'
  | '%2Bcreated_at'
  | '-liked_at'
  | '%2Bliked_at';
export const resourceSortTypes: ResourceSortType[] = [
  '-likes',
  '%2Blikes',
  '-views',
  '%2Bviews',
  '-downloads',
  '%2Bdownloads',
  '-created_at',
  '%2Bcreated_at',
];
export type UsersSortType = 'default' | '%2Bregister_date' | '-register_date';
export const usersSortTypes: UsersSortType[] = [
  '-register_date',
  '%2Bregister_date',
];

export type SpecialParams = 'only_uploaded' | 'only_liked' | 'similar_to';
export type CategoryResourceNames = 'photos' | 'vectors' | 'illustrations';

// По сути то, что рисуется в GridSection
// Когда запрашивается список ресурсов, он может быть сохранен
// В объекте стора под ключем из списка ResourceName, search используется
// Для общего случая на страницах search и main
export type ResourceName = SpecialParams | CategoryResourceNames | 'search';
export const allResourceNames: ResourceName[] = [
  'only_uploaded',
  'only_liked',
  'similar_to',
  'photos',
  'vectors',
  'illustrations',
  'search',
];

export type Special = {
  name: ResourceName;
  value: string;
} | null;

export type CustomError<T> = {
  data?: T;
  errorMessage?: string;
  status?: number;
};

// ================================= Локальные состояния
export type Result = {
  type: 'success' | 'error';
  message: string;
  redirect?: string;
} | null;

type CommonStateInterface = {
  globalLoadingCounter: number;
  result: Result;
  isResultFrozen: boolean;
};

export type PageState = CommonStateInterface & {
  isLikePending: boolean;
  pendingLikeId?: string;
  resources: ResourcesPages;
  lastFetchedItem: Resource | null;
  totalPages: number;
  categories: Categories;
  imagesLimit: number;
};

export type AuthState = CommonStateInterface & {
  isAuth: boolean;
  isPending: boolean;
  rememberMe: boolean;
  user: User | null;
  tokens: TokensManager;
};

export type AdminState = CommonStateInterface & {
  isVerdictPending: boolean;
  pendingVerdicts: { [x: string]: UpdateUserRequest };
  users: Users | null;
  total: number | null;
  error: CustomError<Boolean> | null;
};

export type TransitionState = {
  uploadedFiles: UploadItems;
  avatarFile: UploadItems;
  globalModal: null | React.ReactElement;
  currentKeyHandlers: string[];
};

export type SearchParamsState = {
  tags: Tags;
};

export type TagsState = {
  topTags: Tags;
  startTags: Tags;
  result: Result;

  page: TagsPage | null;

  isPending: {
    match: boolean;
    start: boolean;
  };
};

// ================================= Сущности

export type PossibleAction =
  | 'delete'
  | 'edit'
  | 'download'
  | 'replace'
  | 'toProfile'
  | 'toTags'
  | 'toAdmin'
  | 'toUpload';

export type GridItem = {
  id: string;
  src: string;
  thumbnail: string;
  thumbnailWidth: number;
  thumbnailHeight: number;
  customOverlay: ReactNode;
};

export type Resource = {
  _id: string;
  name: string;
  category: CategoryId;

  preview_link: string;
  thumbnail_link: string;
  link: string;

  thumbnail_width: number;
  thumbnail_height: number;

  tags?: Tags;

  is_liked: boolean;
  likes?: number;
  views?: number;
  downloads?: number;
  created_at?: number;
  owner_id?: string;
};
export type Resources = {
  [id: string]: Resource;
};

export type Tag = {
  name: string;
  amount?: number;
  isNew?: boolean;
};
export type Tags = {
  [id: string]: Tag;
};

export type CategoryId = 'Фотографии' | 'Векторная графика' | 'Иллюстрации';
export const allCategories = ['Фотографии', 'Векторная графика', 'Иллюстрации'];
export type Category = {
  id: CategoryId;
  href: string;
};
export type Categories = {
  [x in CategoryId]: Category;
};

export type Role = 'admin' | 'editor' | 'viewer';

export type User = {
  name: string;
  surname: string;
  organization: string;
  avatar: string | null;
  email: string;
  is_active: boolean;
  role: Role;
  _id: string;
  register_date: number;
};

type Users = {
  [user_id: string]: User;
};

export type FileItem = {
  id: string;
  originalName: string;
  src: string;
  tags: Tags;
  binary: string | ArrayBuffer | null;
  file: File;
};
export type UploadItems = {
  [id: string]: FileItem;
};

export type Total = {
  total: number;
};

export type ResourcesPage = Total & {
  data: Resources;
  requestMeta: ResourceRequest;
  responseTotal: number;
};
export type ResourcesPages = {
  [name in ResourceName]?: ResourcesPage;
};

export type TagsPage = Total & {
  data: Tags;
};

export type MatchPosition = 'any' | 'start';

// ================================= API

export type OkResponse = {
  ok: true;
};

export type MatchedRequest = {
  search: string;
  position: MatchPosition;
};

export type ResourceRequest = OffsetLimit & {
  _saveName: ResourceName;
  special: Special;
  tags: string | null;
  category: CategoryId | null;
  sort: ResourceSortType;
};

export type TagsRequest = {};

export type Tokens = {
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
};

export type EditTagRequest = {
  old: Tag;
  new: Tags;
};

export type VerdictRequest = {
  _id: string;
  verdict: boolean;
};

export type UpdateUserRequest = {
  _id: string;
  name?: string;
  surname?: string;
  organization?: string;
  avatar?: string | null;
  email?: string;
  is_active?: boolean;
  role?: Role;
};

export type UpdateAvatarRequest = {
  _id: string;
  avatar: FormData;
};

export type SignupRequest = {
  name: string;
  surname: string;
  organization: string;
  email: string;
  password: string;
};

export type SigninRequest = {
  username: string;
  password: string;
};

export type SigninResponse = Tokens & {
  user: User;
};

export type TagsResponse = {
  data: Tags;
};

export type LikeRequest = OnlyId & {
  liked: boolean;
};

export type DeleteRequest = OnlyId & {
  redirrect?: string;
};

export type EditRequest = {
  id: string;
  params: {
    tags: Tags;
  };
};
export type EditResponse = {
  id: string;
  tags: Tags;
};

export type UsersResponse = {
  data: Users;
  total: number;
};
export type UsersRequest = OffsetLimit & {
  type: 'existing' | 'not_existing';
  sort: UsersSortType;
};

export type ChangeRoleRequest = {
  _id: string;
  role: Role;
};

export type OnlyId = {
  _id: string;
};

export type UploadRequest = {
  formData: FormData;
};
export type UploadResponse = {
  item: Resource;
};
